/**
 * DraggrContext.tsx - DraggrContext - React context for Draggr
 * 
 * CHANGE HISTORY:
 * - 251021_2025 [cc6aae8] danielbeer: fix: Resolve TypeScript migration errors
 * - 251021_0543 [d4517d3] danielbeer: feat: apply performance optimizations to all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { createContext, useContext, useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { DraggrManager } from './DraggrManager';
import { FileSourceManager } from './FileSourceManager';
// TODO: Create adapters directory
// import {
//   RestAPIAdapter,
//   GraphQLAdapter,
//   FileMakerAdapter,
//   LocalFileAdapter,
//   S3Adapter
// } from './adapters/index';

const DraggrContext = createContext(null);

export const DraggrProvider = ({ children, options = {}, actr = null }) => {
  // Actr is now passed as a prop instead of using useActr hook
  const draggrRef = useRef(null);
  const sourceManagerRef = useRef(null);
  const [, forceUpdate] = useState({});
  
  // Initialize Draggr
  if (!draggrRef.current) {
    draggrRef.current = new DraggrManager({ ...options, actr });
    sourceManagerRef.current = new FileSourceManager(options.fileSource);
    
    // TODO: Register default adapters when adapters are created
    // const adapters = {
    //   rest: new RestAPIAdapter(),
    //   graphql: new GraphQLAdapter(),
    //   filemaker: new FileMakerAdapter(),
    //   local: new LocalFileAdapter(),
    //   s3: new S3Adapter()
    // };
    // 
    // Object.entries(adapters).forEach(([type, adapter]) => {
    //   draggrRef.current.registerAdapter(type, adapter);
    //   sourceManagerRef.current.registerAdapter(type, adapter);
    // });
  }
  
  const draggr = draggrRef.current;
  const sourceManager = sourceManagerRef.current;
  
  // Subscribe to changes
  useEffect(() => {
    const unsubscribe = draggr.subscribe(() => {
      forceUpdate({});
    });
    
    return unsubscribe;
  }, [draggr]);
  
  // Define callbacks outside useMemo (hooks can't be called inside useMemo)
  const makeDraggable = useCallback((itemId, config) => draggr.makeDraggable(itemId, config), [draggr]);
  const makeDroppable = useCallback((zoneId, config) => draggr.makeDroppable(zoneId, config), [draggr]);
  const selectItem = useCallback((itemId, add) => draggr.selectItem(itemId, add), [draggr]);
  const deselectItem = useCallback((itemId) => draggr.deselectItem(itemId), [draggr]);
  const clearSelection = useCallback(() => draggr.clearSelection(), [draggr]);
  const getSelectedItems = useCallback(() => draggr.getSelectedItems(), [draggr]);
  const checkAccess = useCallback((source) => sourceManager.checkAccess(source), [sourceManager]);
  const selectBestSource = useCallback((sources) => sourceManager.selectBestSource(sources), [sourceManager]);
  const fetchFile = useCallback((source, opts) => sourceManager.fetchFile(source, opts), [sourceManager]);
  const fetchFromSources = useCallback((sources, opts) => sourceManager.fetchFromSources(sources, opts), [sourceManager]);
  const streamFile = useCallback((source, onProgress) => sourceManager.streamFile(source, onProgress), [sourceManager]);
  const supportsP2P = useCallback((source) => sourceManager.supportsP2P(source), [sourceManager]);
  const clearCache = useCallback(() => sourceManager.clearCache(), [sourceManager]);
  const registerAdapter = useCallback((type, adapter) => {
    draggr.registerAdapter(type, adapter);
    sourceManager.registerAdapter(type, adapter);
  }, [draggr, sourceManager]);
  
  const value = useMemo(() => ({
    draggr,
    sourceManager,
    makeDraggable,
    makeDroppable,
    selectItem,
    deselectItem,
    clearSelection,
    getSelectedItems,
    checkAccess,
    selectBestSource,
    fetchFile,
    fetchFromSources,
    streamFile,
    supportsP2P,
    clearCache,
    registerAdapter
  }), [draggr, sourceManager, makeDraggable, makeDroppable, selectItem, deselectItem, clearSelection, getSelectedItems, checkAccess, selectBestSource, fetchFile, fetchFromSources, streamFile, supportsP2P, clearCache, registerAdapter]);
  
  return (
    <DraggrContext.Provider value={value}>
      {children}
    </DraggrContext.Provider>
  );
};

export const useDraggr = () => {
  const context = useContext(DraggrContext);
  
  if (!context) {
    throw new Error('useDraggr must be used within DraggrProvider');
  }
  
  return context;
};

/**
 * Hook to make an element draggable
 */
export const useDraggable = (itemId, config = {}) => {
  try {
    const { makeDraggable } = useDraggr();
    return makeDraggable(itemId, config);
  } catch (e) {
    // DraggrProvider not available - return empty props (drag disabled)
    return {};
  }
};

/**
 * Hook to make an element droppable
 */
export const useDroppable = (zoneId, config = {}) => {
  try {
    const { makeDroppable } = useDraggr();
    return makeDroppable(zoneId, config);
  } catch (e) {
    // DraggrProvider not available - return empty props (drop disabled)
    return {};
  }
};

/**
 * Hook to export file from source
 */
export const useFileExport = () => {
  const { fetchFile, fetchFromSources, checkAccess } = useDraggr();
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const exportFile = useCallback(async (source, fileName) => {
    setLoading(true);
    setError(null);
    setProgress(0);
    
    try {
      // Check access
      const access = await checkAccess(source);
      
      if (!access.canRead) {
        throw new Error('No read access to file');
      }
      
      // Fetch file
      const blob = await fetchFile(source, {
        onProgress: (p) => setProgress(p.percent)
      });
      
      // Create download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setLoading(false);
      return blob;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, [fetchFile, checkAccess]);
  
  const exportFromSources = useCallback(async (sources, fileName) => {
    setLoading(true);
    setError(null);
    setProgress(0);
    
    try {
      const blob = await fetchFromSources(sources, {
        onProgress: (p) => setProgress(p.percent)
      });
      
      // Create download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setLoading(false);
      return blob;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, [fetchFromSources]);
  
  return {
    exportFile,
    exportFromSources,
    progress,
    loading,
    error
  };
};

export default DraggrContext;
